import type { QueryFunction, QueryKey } from "@tanstack/react-query";
import { API_BASE_URL } from "@/services";
import { riskQueryKeys } from "@/constants/queryKeys";
import type {
  PortfolioCurrentResponse,
  PortfolioHistoryMetricResponse,
  AlertPolicy,
  AlertPoliciesResponse,
  AlertPolicyCreateRequest,
  AlertEvent,
  AlertEventsResponse,
} from "@/types/risk";
import { mapAlertEvent } from "@/utils/risk";

const jsonHeaders = { "Content-Type": "application/json" } as const;

const extractErrorMessage = async (response: Response, fallback: string): Promise<string> => {
  try {
    const cloned = response.clone();
    try {
      const data = await cloned.json();
      const detail = (data && (data.detail || data.message)) as unknown;
      if (typeof detail === "string" && detail.trim().length > 0) return detail;
    } catch {
      /* intentionally ignored: fall back to text parsing */
    }
    const text = await response.text();
    if (text && text.trim().length > 0) {
      try {
        const parsed = JSON.parse(text);
        const detail = (parsed && (parsed.detail || parsed.message)) as unknown;
        if (typeof detail === "string" && detail.trim().length > 0) return detail;
      } catch {
        /* intentionally ignored: plain text body */
      }
      return text.trim();
    }
  } catch {
    /* intentionally ignored: return fallback message */
  }
  return fallback;
};
// ================= New Endpoints =================

// GET /risk/portfolio/current
export const portfolioCurrentQueryOptions = (authToken?: string) => ({
  queryKey: riskQueryKeys.portfolioCurrent(),
  queryFn: (async ({ signal }) => {
    const url = new URL(`${API_BASE_URL}/risk/portfolio/current`);
    const response = await fetch(url.toString(), {
      signal,
      headers: {
        ...jsonHeaders,
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch portfolio current metrics: ${response.status}`);
    }
    return (await response.json()) as PortfolioCurrentResponse;
  }) as QueryFunction<PortfolioCurrentResponse, QueryKey>,
  enabled: Boolean(authToken),
});

// GET /risk/portfolio/history/{metric_key}
export const portfolioHistoryMetricQueryOptions = (metricKey: string, authToken?: string) => ({
  queryKey: riskQueryKeys.portfolioHistoryMetric(metricKey),
  queryFn: (async ({ signal }) => {
    const url = new URL(`${API_BASE_URL}/risk/portfolio/history/${encodeURIComponent(metricKey)}`);
    const response = await fetch(url.toString(), {
      signal,
      headers: {
        ...jsonHeaders,
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch portfolio history for ${metricKey}: ${response.status}`);
    }
    return (await response.json()) as PortfolioHistoryMetricResponse;
  }) as QueryFunction<PortfolioHistoryMetricResponse, QueryKey>,
  enabled: Boolean(authToken && metricKey && metricKey.length > 0),
});

// GET /api/v1/risk/alerts/policies
export const alertPoliciesQueryOptions = (authToken?: string) => ({
  queryKey: riskQueryKeys.policies(),
  queryFn: (async ({ signal }) => {
    const url = new URL(`${API_BASE_URL}/risk/alerts/policies`);
    const response = await fetch(url.toString(), {
      signal,
      headers: {
        ...jsonHeaders,
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch alert policies: ${response.status}`);
    }
    const json = (await response.json()) as AlertPoliciesResponse | AlertPolicy[];
    // Backend may return either { data: [...] } or just array
    if (Array.isArray(json)) {
      return { data: json } as AlertPoliciesResponse;
    }
    return json as AlertPoliciesResponse;
  }) as QueryFunction<AlertPoliciesResponse, QueryKey>,
  enabled: Boolean(authToken),
});

// POST /api/v1/risk/alerts/policies
export const createAlertPolicy = async (params: { body: AlertPolicyCreateRequest; authToken?: string }): Promise<AlertPolicy> => {
  const url = new URL(`${API_BASE_URL}/risk/alerts/policies`);
  const response = await fetch(url.toString(), {
    method: "POST",
    headers: {
      ...jsonHeaders,
      ...(params.authToken ? { Authorization: `Bearer ${params.authToken}` } : {}),
    },
    body: JSON.stringify(params.body ?? {}),
  });
  if (!response.ok) {
    const message = await extractErrorMessage(response, `Failed to create alert policy: ${response.status}`);
    throw new Error(message);
  }
  return (await response.json()) as AlertPolicy;
};

// PUT /api/v1/risk/alerts/policies/{policy_id}
export const updateAlertPolicy = async (params: { policyId: string; body: Partial<AlertPolicyCreateRequest> & { enabled?: boolean | null }; authToken?: string }): Promise<AlertPolicy> => {
  const url = new URL(`${API_BASE_URL}/risk/alerts/policies/${encodeURIComponent(params.policyId)}`);
  const response = await fetch(url.toString(), {
    method: "PUT",
    headers: {
      ...jsonHeaders,
      ...(params.authToken ? { Authorization: `Bearer ${params.authToken}` } : {}),
    },
    body: JSON.stringify(params.body ?? {}),
  });
  if (!response.ok) {
    const message = await extractErrorMessage(response, `Failed to update alert policy: ${response.status}`);
    throw new Error(message);
  }
  return (await response.json()) as AlertPolicy;
};

// DELETE /api/v1/risk/alerts/policies/{policy_id}
export const deleteAlertPolicy = async (params: { policyId: string; authToken?: string }): Promise<{ ok: true }> => {
  const url = new URL(`${API_BASE_URL}/risk/alerts/policies/${encodeURIComponent(params.policyId)}`);
  const response = await fetch(url.toString(), {
    method: "DELETE",
    headers: {
      ...(params.authToken ? { Authorization: `Bearer ${params.authToken}` } : {}),
    },
  });
  if (!response.ok) {
    const message = await extractErrorMessage(response, `Failed to delete alert policy: ${response.status}`);
    throw new Error(message);
  }
  return { ok: true } as const;
};

// GET /api/v1/risk/alerts/events?limit=100
export const alertEventsQueryOptions = (params: { authToken?: string; limit?: number } = {}) => ({
  queryKey: riskQueryKeys.events(params.limit != null ? { limit: params.limit } : undefined),
  queryFn: (async ({ signal }) => {
    const limit = Number.isFinite(params.limit as number) ? params.limit : 100;
    const url = new URL(`${API_BASE_URL}/risk/alerts/events`);
    if (limit) url.searchParams.set("limit", String(limit));
    const response = await fetch(url.toString(), {
      signal,
      headers: {
        ...jsonHeaders,
        ...(params.authToken ? { Authorization: `Bearer ${params.authToken}` } : {}),
      },
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch alert events: ${response.status}`);
    }
    const json = (await response.json()) as AlertEventsResponse | (unknown[] & { evaluation_status?: unknown });
    const rawArray = Array.isArray(json) ? (json as unknown[]) : (json as AlertEventsResponse).data;
    const mapped: AlertEvent[] = rawArray.map(mapAlertEvent);
    const evaluation_status = Array.isArray(json) ? undefined : (json as AlertEventsResponse).evaluation_status;

    return { data: mapped, evaluation_status } as AlertEventsResponse;
  }) as QueryFunction<AlertEventsResponse, QueryKey>,
  enabled: Boolean(params.authToken),
});

// POST /api/v1/risk/alerts/events/{event_id}/ack
export const ackAlertEvent = async (params: { eventId: string; authToken?: string }): Promise<AlertEvent> => {
  const url = new URL(`${API_BASE_URL}/risk/alerts/events/${encodeURIComponent(params.eventId)}/ack`);
  const response = await fetch(url.toString(), {
    method: "POST",
    headers: {
      ...jsonHeaders,
      ...(params.authToken ? { Authorization: `Bearer ${params.authToken}` } : {}),
    },
  });
  if (!response.ok) {
    const message = await extractErrorMessage(response, `Failed to acknowledge alert event: ${response.status}`);
    throw new Error(message);
  }
  const body = await response.json();
  const data = (body && body.data) ?? body;
  return mapAlertEvent(data);
};

// POST /api/v1/risk/alerts/events/{event_id}/unack
export const unackAlertEvent = async (params: { eventId: string; authToken?: string }): Promise<AlertEvent> => {
  const url = new URL(`${API_BASE_URL}/risk/alerts/events/${encodeURIComponent(params.eventId)}/unack`);
  const response = await fetch(url.toString(), {
    method: "POST",
    headers: {
      ...jsonHeaders,
      ...(params.authToken ? { Authorization: `Bearer ${params.authToken}` } : {}),
    },
  });
  if (!response.ok) {
    const message = await extractErrorMessage(response, `Failed to unacknowledge alert event: ${response.status}`);
    throw new Error(message);
  }
  const body = await response.json();
  const data = (body && body.data) ?? body;
  return mapAlertEvent(data);
};

